import { type ReactNode, useEffect, useRef } from 'react';

import { ModalRegistry } from './modal-registry';
import { ModalManagerContext } from './ModalManagerContext';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface ModalManagerProviderProps {
  children: ReactNode;
}

/**
 * Provides a ModalRegistry to the React subtree and handles the global Esc key.
 *
 * Mount ONCE inside ERPCoreProvider (nested inside FocusManagerProvider so that
 * dialog focus restoration coordinates correctly).
 *
 * ## Esc key behaviour
 *
 * A single `keydown` listener is attached in **capture phase** so it fires
 * before React's synthetic events and before the ShortcutManager's bubble-phase
 * listeners. This ensures the topmost modal always receives Esc first.
 *
 * `event.preventDefault()` is called synchronously so the browser never sees
 * the Esc event. Then the async close guard runs (which may open a secondary
 * "Discard changes?" confirm dialog). If the guard passes, `onClose` is called
 * — which typically sets the controlling `isOpen` state to false.
 *
 * ## Nested modals
 *
 * Only the topmost modal receives Esc. A confirm dialog opened as a guard side-
 * effect pushes itself to the top of the stack, so the next Esc goes to it —
 * this is the correct ERP "nested dialog" behaviour.
 */
export function ModalManagerProvider({ children }: ModalManagerProviderProps) {
  // One registry per app — created once, never replaced
  const registryRef = useRef<ModalRegistry | null>(null);
  registryRef.current ??= new ModalRegistry();
  const registry = registryRef.current;

  // ── Global Esc handler ────────────────────────────────────────────────────
  useEffect(() => {
    // Async event listeners are standard JavaScript — event.preventDefault()
    // is called synchronously before any await, which is all the browser needs.
    const handleKeyDown = async (event: KeyboardEvent): Promise<void> => {
      if (event.key !== 'Escape') return;

      const topmost = registry.getTopmost();
      if (!topmost?.closeOnEscape) return;

      // Suppress browser default (e.g. exiting fullscreen) synchronously
      event.preventDefault();
      // Stop propagation so ShortcutManager and other keydown handlers
      // don't also try to handle Esc for this modal
      event.stopPropagation();

      // Async guard + onClose notification
      await registry.closeTopmost();
    };

    // Wrapper: async listeners trigger @typescript-eslint/no-misused-promises
    const onKeyDown = (event: KeyboardEvent) => {
      void handleKeyDown(event);
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });

    return () => {
      document.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [registry]);

  return (
    <ModalManagerContext.Provider value={{ registry }}>{children}</ModalManagerContext.Provider>
  );
}
